import type { RenderClipRect, RenderNode } from "./types.js";

/** Multiplier for nesting local z-index values into a global paint order. */
export const Z_INDEX_SCOPE_SIZE = 100;

type BoundsRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

function clipRectToBounds(rect: RenderClipRect): BoundsRect {
  return {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height,
  };
}

function boundsToClipRect(
  bounds: BoundsRect,
  ownerNodeId: string,
): RenderClipRect {
  return {
    ownerNodeId,
    x: bounds.left,
    y: bounds.top,
    width: Math.max(0, bounds.right - bounds.left),
    height: Math.max(0, bounds.bottom - bounds.top),
  };
}

function intersectBounds(a: BoundsRect, b: BoundsRect): BoundsRect {
  return {
    left: Math.max(a.left, b.left),
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
  };
}

/**
 * Intersects multiple canvas-space clip rectangles into a single bounds rect.
 */
export function intersectRenderClipRects(
  rects: RenderClipRect[],
): RenderClipRect | null {
  const first = rects[0];
  if (!first) {
    return null;
  }

  let bounds = clipRectToBounds(first);

  for (let index = 1; index < rects.length; index += 1) {
    const next = rects[index];
    if (!next) {
      continue;
    }
    bounds = intersectBounds(bounds, clipRectToBounds(next));
  }

  if (bounds.right <= bounds.left || bounds.bottom <= bounds.top) {
    return null;
  }

  const last = rects[rects.length - 1] ?? first;
  return boundsToClipRect(bounds, last.ownerNodeId);
}

/**
 * Shifts a render node and descendants flattened into the same DOM coordinate
 * space. Flex containers and lists own a DOM shell, so their children stay local.
 */
export function offsetRenderNodePosition(
  node: RenderNode,
  offsetX: number,
  offsetY: number,
): RenderNode {
  return {
    ...node,
    x: node.x + offsetX,
    y: node.y + offsetY,
    ...(node.clipRects
      ? {
          clipRects: node.clipRects.map((rect) => ({
            ...rect,
            x: rect.x + offsetX,
            y: rect.y + offsetY,
          })),
        }
      : {}),
    ...(node.kind === "container" && node.layout !== "flex"
      ? {
          children: node.children.map((child) =>
            offsetRenderNodePosition(child, offsetX, offsetY),
          ),
        }
      : {}),
  };
}

/**
 * Appends a canvas-space clip rectangle to a single render node.
 */
export function applyClipRectToRenderNode(
  node: RenderNode,
  clipRect: RenderClipRect,
): RenderNode {
  return {
    ...node,
    clipRects: [...(node.clipRects ?? []), clipRect],
  };
}

/**
 * Maps a direct child's local z-index into the parent's flattened stacking scope.
 */
export function computeEffectiveChildZIndex(
  parentZIndex: number,
  childLocalZIndex: number,
): number {
  return parentZIndex * Z_INDEX_SCOPE_SIZE + childLocalZIndex;
}

/**
 * Scales a root render node z-index into the same scope used by flattened
 * absolute container children, so child local z-index values never escape
 * above higher root-level siblings.
 */
export function scaleRootRenderNodeZIndex<T extends RenderNode>(node: T): T {
  return {
    ...node,
    zIndex: node.zIndex * Z_INDEX_SCOPE_SIZE,
  };
}

/**
 * Prepares direct children of an absolute container for flat (sibling) rendering:
 * canvas-space position, inherited clip rect, and effective z-index stacking.
 */
export function prepareAbsoluteContainerChildren(
  children: RenderNode[],
  containerX: number,
  containerY: number,
  containerId: string,
  containerWidth: number,
  containerHeight: number,
  containerZIndex: number,
): RenderNode[] {
  const clipRect: RenderClipRect = {
    ownerNodeId: containerId,
    x: containerX,
    y: containerY,
    width: containerWidth,
    height: containerHeight,
  };

  return children.map((child) => {
    const positionedChild = offsetRenderNodePosition(
      child,
      containerX,
      containerY,
    );
    const clippedChild = applyClipRectToRenderNode(positionedChild, clipRect);

    return {
      ...clippedChild,
      zIndex: computeEffectiveChildZIndex(
        containerZIndex,
        clippedChild.zIndex ?? 0,
      ),
    };
  });
}
